import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './Purchase.module.css';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiShoppingCart } from 'react-icons/fi';
import Moment from 'moment';
import { useBusinessSettings } from '../../context/BusinessSettingsContext';

/**
 * Add Purchase Modal - Inline Component
 */
function AddPurchaseModal({ setOpenAddModal, setRefresh, refresh }) {
    const { isFeatureEnabled } = useBusinessSettings();
    const [ProductName, setProductName] = useState('');
    const [Quantity, setQuantity] = useState('');
    const [Price, setPrice] = useState('');
    const [ExtraCost, setExtraCost] = useState('0');
    const [Supplier, setSupplier] = useState('');
    const [SupplierNumber, setSupplierNumber] = useState('');
    const [Description, setDescription] = useState('');
    const [WareHoue, setWareHoue] = useState('');
    const [Status, setStatus] = useState(0);
    const [PaynemtStatus, setPaynemtStatus] = useState(0);
    const [Photo, setPhoto] = useState('');
    const [loading, setLoading] = useState(false);
    const [PurchaseDate, setPurchaseDate] = useState(new Date());
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paidAmount, setPaidAmount] = useState('');

    const uid = localStorage.getItem('id');

    const calculateTotalAmount = () => {
        const qty = parseFloat(Quantity || 0);
        const price = parseFloat(Price || 0);
        const extra = parseFloat(ExtraCost || 0);
        return qty * price + extra;
    };

    const calculateRemainingAmount = () => {
        const total = calculateTotalAmount();
        const paid = parseFloat(paidAmount || 0);
        return total - paid;
    };

    // Validation and sanitization function
    const validatePurchaseForm = () => {
        // Required field validation
        if (!ProductName || !ProductName.trim()) {
            toast.error('Product Name is required');
            return false;
        }
        if (ProductName.trim().length > 200) {
            toast.error('Product Name must be less than 200 characters');
            return false;
        }

        if (!Supplier || !Supplier.trim()) {
            toast.error('Supplier Name is required');
            return false;
        }
        if (Supplier.trim().length > 200) {
            toast.error('Supplier Name must be less than 200 characters');
            return false;
        }

        // Numeric field validation
        if (!Quantity || isNaN(Quantity) || parseFloat(Quantity) <= 0) {
            toast.error('Quantity must be a positive number');
            return false;
        }
        if (parseFloat(Quantity) > 1000000) {
            toast.error('Quantity cannot exceed 1,000,000');
            return false;
        }

        if (!Price || isNaN(Price) || parseFloat(Price) <= 0) {
            toast.error('Unit Price must be a positive number');
            return false;
        }
        if (parseFloat(Price) > 100000000) {
            toast.error('Price cannot exceed 100,000,000');
            return false;
        }

        if (ExtraCost && (isNaN(ExtraCost) || parseFloat(ExtraCost) < 0)) {
            toast.error('Extra Cost must be a non-negative number');
            return false;
        }
        if (parseFloat(ExtraCost || 0) > 10000000) {
            toast.error('Extra Cost cannot exceed 10,000,000');
            return false;
        }

        // Phone number validation
        if (SupplierNumber && SupplierNumber.trim()) {
            const phoneRegex = /^[0-9+\-\s()]+$/;
            if (!phoneRegex.test(SupplierNumber)) {
                toast.error('Supplier Phone Number contains invalid characters');
                return false;
            }
            if (SupplierNumber.trim().length > 20) {
                toast.error('Supplier Phone Number must be less than 20 characters');
                return false;
            }
        }

        // Description length validation
        if (Description && Description.trim().length > 1000) {
            toast.error('Description must be less than 1000 characters');
            return false;
        }

        // Warehouse/Location length validation
        if (WareHoue && WareHoue.trim().length > 200) {
            toast.error('Warehouse/Location must be less than 200 characters');
            return false;
        }

        // Photo URL validation
        if (Photo && Photo.trim()) {
            if (Photo.trim().length > 500) {
                toast.error('Photo URL must be less than 500 characters');
                return false;
            }
            // Basic URL validation
            try {
                if (Photo.includes('http')) {
                    new URL(Photo);
                }
            } catch (e) {
                toast.error('Photo URL is invalid');
                return false;
            }
        }

        // Date validation
        if (PurchaseDate > new Date()) {
            toast.error("You can't set a future date");
            return false;
        }

        // Cash flow validation
        if (isFeatureEnabled('enableCashFlow')) {
            if (paidAmount && (isNaN(paidAmount) || parseFloat(paidAmount) < 0)) {
                toast.error('Paid Amount must be a non-negative number');
                return false;
            }
            if (parseFloat(paidAmount || 0) > calculateTotalAmount()) {
                toast.error('Paid Amount cannot exceed Total Amount');
                return false;
            }
        }

        return true;
    };

    const submitPurchase = () => {
        setLoading(true);

        // Validate form
        if (!validatePurchaseForm()) {
            return setLoading(false);
        }

        const formData = {
            uid,
            ProductName: ProductName.trim(),
            Quantity,
            Price,
            ExtraCost: ExtraCost || '0',
            Supplier: Supplier.trim(),
            SupplierNumber: SupplierNumber.trim(),
            Description: Description.trim(),
            Photo: Photo.trim(),
            WareHoue: WareHoue.trim(),
            Status,
            PaynemtStatus,
            PurchaseDate,
            ...(isFeatureEnabled('enableCashFlow') && {
                paymentMethod,
                paidAmount: parseFloat(paidAmount || 0),
                totalAmount: calculateTotalAmount(),
                remainingAmount: calculateRemainingAmount(),
                cashImpact: paymentMethod === 'cash' ? -parseFloat(paidAmount || 0) : 0,
                addedBy: uid,
            }),
        };

        console.log('DEBUG: Submitting purchase with formData:', formData);

        AxiosUrl.post(`/add-purchase`, { data: formData })
            .then((res) => {
                console.log('DEBUG: Backend response:', res.data);
                if (res.data.success) {
                    console.log('DEBUG: Purchase added successfully, saved data:', res.data.data);
                    toast.success(res.data.message || 'Purchase Added Successfully');
                    setOpenAddModal(false);
                    setRefresh(refresh + 1);
                } else {
                    console.error('DEBUG: Failed to add purchase:', res.data.message);
                    toast.error(res.data.message || 'Failed to add purchase');
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('DEBUG: Error adding purchase:', error);
                console.log(error);
                toast.error('Error adding purchase');
                setLoading(false);
            });
    };

    return (
        <div className={styles.modalOverlay} onClick={() => setOpenAddModal(false)}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>Add Purchase Order</h2>
                    <button className={styles.closeBtn} onClick={() => setOpenAddModal(false)}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); submitPurchase(); }} className={styles.modalForm}>
                    <div className={styles.formGroup}>
                        <label><i className="fas fa-box"></i> Product Name *</label>
                        <input
                            type="text"
                            placeholder="Enter product name"
                            onChange={(e) => setProductName(e.target.value)}
                            value={ProductName}
                            required
                            maxLength="200"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-truck"></i> Supplier Name *</label>
                        <input
                            type="text"
                            placeholder="Enter supplier name"
                            onChange={(e) => setSupplier(e.target.value)}
                            value={Supplier}
                            required
                            maxLength="200"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-phone"></i> Supplier Phone Number</label>
                        <input
                            type="text"
                            placeholder="Enter supplier phone number"
                            onChange={(e) => setSupplierNumber(e.target.value)}
                            value={SupplierNumber}
                            maxLength="20"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-sort-numeric-up"></i> Quantity *</label>
                        <input
                            type="number"
                            placeholder="Enter quantity"
                            onChange={(e) => setQuantity(e.target.value)}
                            value={Quantity}
                            required
                            min="0.01"
                            max="1000000"
                            step="any"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-dollar-sign"></i> Unit Price *</label>
                        <input
                            type="number"
                            placeholder="Enter unit price"
                            onChange={(e) => setPrice(e.target.value)}
                            value={Price}
                            required
                            min="0.01"
                            max="100000000"
                            step="any"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-plus-circle"></i> Extra Cost (Shipping, Tax, etc.)</label>
                        <input
                            type="number"
                            placeholder="Enter extra cost"
                            onChange={(e) => setExtraCost(e.target.value)}
                            value={ExtraCost}
                            min="0"
                            max="10000000"
                            step="any"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-warehouse"></i> Warehouse/Location</label>
                        <input
                            type="text"
                            placeholder="Enter warehouse location"
                            onChange={(e) => setWareHoue(e.target.value)}
                            value={WareHoue}
                            maxLength="200"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-align-left"></i> Description</label>
                        <textarea
                            rows="3"
                            placeholder="Enter description"
                            onChange={(e) => setDescription(e.target.value)}
                            value={Description}
                            maxLength="1000"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-image"></i> Product Photo URL</label>
                        <input
                            type="text"
                            placeholder="Enter product photo URL"
                            onChange={(e) => setPhoto(e.target.value)}
                            value={Photo}
                            maxLength="500"
                        />
                        <small>Optional: Enter image URL or file path</small>
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-check-circle"></i> Purchase Status</label>
                        <select value={Status} onChange={(e) => setStatus(parseInt(e.target.value))}>
                            <option value={0}>Received</option>
                            <option value={1}>Pending</option>
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-money-check-alt"></i> Payment Status</label>
                        <select value={PaynemtStatus} onChange={(e) => setPaynemtStatus(parseInt(e.target.value))}>
                            <option value={0}>Paid</option>
                            <option value={1}>Due</option>
                        </select>
                    </div>

                    {isFeatureEnabled('enableCashFlow') && (
                        <>
                            <div className={styles.formGroup}>
                                <label><i className="fas fa-credit-card"></i> Payment Method</label>
                                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                                    <option value="cash">Cash</option>
                                    <option value="bank">Bank</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label><i className="fas fa-money-bill-wave"></i> Paid Amount</label>
                                <input
                                    type="number"
                                    placeholder="Enter paid amount"
                                    value={paidAmount}
                                    onChange={(e) => setPaidAmount(e.target.value)}
                                    min="0"
                                    step="any"
                                />
                                {paidAmount && (
                                    <small>Remaining: ৳{calculateRemainingAmount().toFixed(2)}</small>
                                )}
                            </div>
                        </>
                    )}

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-calendar-alt"></i> Purchase Date</label>
                        <DatePicker
                            className={styles.datePicker}
                            selected={PurchaseDate}
                            onChange={(date) => setPurchaseDate(date)}
                            dateFormat="dd/MM/yyyy"
                            maxDate={new Date()}
                        />
                        <small>If you want to set any previous date then select from the below, by default it will pick today's date</small>
                    </div>

                    <div className={styles.modalActions}>
                        <button type="button" className={styles.cancelBtn} onClick={() => setOpenAddModal(false)}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitBtn} disabled={loading}>
                            {loading ? 'Adding...' : 'Add Purchase'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/**
 * Edit Purchase Modal - Inline Component
 */
function EditPurchaseModal({ setOpenEditModal, data, setRefresh, refresh }) {
    const { isFeatureEnabled } = useBusinessSettings();
    const [ProductName, setProductName] = useState('');
    const [Quantity, setQuantity] = useState('');
    const [Price, setPrice] = useState('');
    const [ExtraCost, setExtraCost] = useState('0');
    const [Supplier, setSupplier] = useState('');
    const [SupplierNumber, setSupplierNumber] = useState('');
    const [Description, setDescription] = useState('');
    const [WareHoue, setWareHoue] = useState('');
    const [Status, setStatus] = useState(0);
    const [PaynemtStatus, setPaynemtStatus] = useState(0);
    const [Photo, setPhoto] = useState('');
    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paidAmount, setPaidAmount] = useState('');

    const uid = localStorage.getItem('id');

    useEffect(() => {
        if (data) {
            setProductName(data.ProductName || '');
            setQuantity(data.Quantity || '');
            setPrice(data.Price || '');
            setExtraCost(data.ExtraCost || '0');
            setSupplier(data.Supplier || '');
            setSupplierNumber(data.SupplierNumber || '');
            setDescription(data.Description || '');
            setWareHoue(data.WareHoue || '');
            setStatus(data.Status || 0);
            setPaynemtStatus(data.PaynemtStatus || 0);
            setPhoto(data.Photo || '');

            if (data.paymentMethod) setPaymentMethod(data.paymentMethod);
            if (data.paidAmount) setPaidAmount(data.paidAmount.toString());
        }
    }, [data]);

    const calculateTotalAmount = () => {
        const qty = parseFloat(Quantity || 0);
        const price = parseFloat(Price || 0);
        const extra = parseFloat(ExtraCost || 0);
        return qty * price + extra;
    };

    const calculateRemainingAmount = () => {
        const total = calculateTotalAmount();
        const paid = parseFloat(paidAmount || 0);
        return total - paid;
    };

    // Validation function for Edit modal
    const validateEditForm = () => {
        // Required field validation
        if (!ProductName || !ProductName.trim()) {
            toast.error('Product Name is required');
            return false;
        }
        if (ProductName.trim().length > 200) {
            toast.error('Product Name must be less than 200 characters');
            return false;
        }

        if (!Supplier || !Supplier.trim()) {
            toast.error('Supplier Name is required');
            return false;
        }
        if (Supplier.trim().length > 200) {
            toast.error('Supplier Name must be less than 200 characters');
            return false;
        }

        // Numeric field validation
        if (!Quantity || isNaN(Quantity) || parseFloat(Quantity) <= 0) {
            toast.error('Quantity must be a positive number');
            return false;
        }
        if (parseFloat(Quantity) > 1000000) {
            toast.error('Quantity cannot exceed 1,000,000');
            return false;
        }

        if (!Price || isNaN(Price) || parseFloat(Price) <= 0) {
            toast.error('Unit Price must be a positive number');
            return false;
        }
        if (parseFloat(Price) > 100000000) {
            toast.error('Price cannot exceed 100,000,000');
            return false;
        }

        if (ExtraCost && (isNaN(ExtraCost) || parseFloat(ExtraCost) < 0)) {
            toast.error('Extra Cost must be a non-negative number');
            return false;
        }
        if (parseFloat(ExtraCost || 0) > 10000000) {
            toast.error('Extra Cost cannot exceed 10,000,000');
            return false;
        }

        // Phone number validation
        if (SupplierNumber && SupplierNumber.trim()) {
            const phoneRegex = /^[0-9+\-\s()]+$/;
            if (!phoneRegex.test(SupplierNumber)) {
                toast.error('Supplier Phone Number contains invalid characters');
                return false;
            }
            if (SupplierNumber.trim().length > 20) {
                toast.error('Supplier Phone Number must be less than 20 characters');
                return false;
            }
        }

        // Description length validation
        if (Description && Description.trim().length > 1000) {
            toast.error('Description must be less than 1000 characters');
            return false;
        }

        // Warehouse/Location length validation
        if (WareHoue && WareHoue.trim().length > 200) {
            toast.error('Warehouse/Location must be less than 200 characters');
            return false;
        }

        // Photo URL validation
        if (Photo && Photo.trim()) {
            if (Photo.trim().length > 500) {
                toast.error('Photo URL must be less than 500 characters');
                return false;
            }
            try {
                if (Photo.includes('http')) {
                    new URL(Photo);
                }
            } catch (e) {
                toast.error('Photo URL is invalid');
                return false;
            }
        }

        // Cash flow validation
        if (isFeatureEnabled('enableCashFlow')) {
            if (paidAmount && (isNaN(paidAmount) || parseFloat(paidAmount) < 0)) {
                toast.error('Paid Amount must be a non-negative number');
                return false;
            }
            if (parseFloat(paidAmount || 0) > calculateTotalAmount()) {
                toast.error('Paid Amount cannot exceed Total Amount');
                return false;
            }
        }

        return true;
    };

    const submitPurchaseEdit = () => {
        setLoading(true);

        // Validate form
        if (!validateEditForm()) {
            return setLoading(false);
        }

        const formData = {
            uid,
            purchaseId: data._id,
            ProductName: ProductName.trim(),
            Quantity,
            Price,
            ExtraCost: ExtraCost || '0',
            Supplier: Supplier.trim(),
            SupplierNumber: SupplierNumber.trim(),
            Description: Description.trim(),
            Photo: Photo.trim(),
            WareHoue: WareHoue.trim(),
            Status,
            PaynemtStatus,
            ...(isFeatureEnabled('enableCashFlow') && {
                paymentMethod,
                paidAmount: parseFloat(paidAmount || 0),
                totalAmount: calculateTotalAmount(),
                remainingAmount: calculateRemainingAmount(),
                cashImpact: paymentMethod === 'cash' ? -parseFloat(paidAmount || 0) : 0,
            }),
        };

        console.log('DEBUG: Updating purchase with formData:', formData);

        AxiosUrl.post(`/edit-purchase`, { data: formData })
            .then((res) => {
                console.log('DEBUG: Backend response (edit):', res.data);
                if (res.data.success) {
                    console.log('DEBUG: Purchase updated successfully');
                    toast.success(res.data.message || 'Purchase Updated Successfully');
                    setOpenEditModal(false);
                    setRefresh(refresh + 1);
                } else {
                    console.error('DEBUG: Failed to update purchase:', res.data.message);
                    toast.error(res.data.message || 'Failed to update purchase');
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('DEBUG: Error updating purchase:', error);
                console.log(error);
                toast.error('Error updating purchase');
                setLoading(false);
            });
    };

    return (
        <div className={styles.modalOverlay} onClick={() => setOpenEditModal(false)}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>Edit Purchase Order</h2>
                    <button className={styles.closeBtn} onClick={() => setOpenEditModal(false)}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); submitPurchaseEdit(); }} className={styles.modalForm}>
                    <div className={styles.formGroup}>
                        <label><i className="fas fa-box"></i> Product Name *</label>
                        <input
                            type="text"
                            placeholder="Enter product name"
                            onChange={(e) => setProductName(e.target.value)}
                            value={ProductName}
                            required
                            maxLength="200"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-truck"></i> Supplier Name *</label>
                        <input
                            type="text"
                            placeholder="Enter supplier name"
                            onChange={(e) => setSupplier(e.target.value)}
                            value={Supplier}
                            required
                            maxLength="200"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-phone"></i> Supplier Phone Number</label>
                        <input
                            type="text"
                            placeholder="Enter supplier phone number"
                            onChange={(e) => setSupplierNumber(e.target.value)}
                            value={SupplierNumber}
                            maxLength="20"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-sort-numeric-up"></i> Quantity *</label>
                        <input
                            type="number"
                            placeholder="Enter quantity"
                            onChange={(e) => setQuantity(e.target.value)}
                            value={Quantity}
                            required
                            min="0.01"
                            max="1000000"
                            step="any"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-dollar-sign"></i> Unit Price *</label>
                        <input
                            type="number"
                            placeholder="Enter unit price"
                            onChange={(e) => setPrice(e.target.value)}
                            value={Price}
                            required
                            min="0.01"
                            max="100000000"
                            step="any"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-plus-circle"></i> Extra Cost (Shipping, Tax, etc.)</label>
                        <input
                            type="number"
                            placeholder="Enter extra cost"
                            onChange={(e) => setExtraCost(e.target.value)}
                            value={ExtraCost}
                            min="0"
                            max="10000000"
                            step="any"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-warehouse"></i> Warehouse/Location</label>
                        <input
                            type="text"
                            placeholder="Enter warehouse location"
                            onChange={(e) => setWareHoue(e.target.value)}
                            value={WareHoue}
                            maxLength="200"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-align-left"></i> Description</label>
                        <textarea
                            rows="3"
                            placeholder="Enter description"
                            onChange={(e) => setDescription(e.target.value)}
                            value={Description}
                            maxLength="1000"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-image"></i> Product Photo URL</label>
                        <input
                            type="text"
                            placeholder="Enter product photo URL"
                            onChange={(e) => setPhoto(e.target.value)}
                            value={Photo}
                            maxLength="500"
                        />
                        <small>Optional: Enter image URL or file path</small>
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-check-circle"></i> Purchase Status</label>
                        <select value={Status} onChange={(e) => setStatus(parseInt(e.target.value))}>
                            <option value={0}>Received</option>
                            <option value={1}>Pending</option>
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label><i className="fas fa-money-check-alt"></i> Payment Status</label>
                        <select value={PaynemtStatus} onChange={(e) => setPaynemtStatus(parseInt(e.target.value))}>
                            <option value={0}>Paid</option>
                            <option value={1}>Due</option>
                        </select>
                    </div>

                    {isFeatureEnabled('enableCashFlow') && (
                        <>
                            <div className={styles.formGroup}>
                                <label><i className="fas fa-credit-card"></i> Payment Method</label>
                                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                                    <option value="cash">Cash</option>
                                    <option value="bank">Bank</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label><i className="fas fa-money-bill-wave"></i> Paid Amount</label>
                                <input
                                    type="number"
                                    placeholder="Enter paid amount"
                                    value={paidAmount}
                                    onChange={(e) => setPaidAmount(e.target.value)}
                                    min="0"
                                    step="any"
                                />
                                {paidAmount && (
                                    <small>Remaining: ৳{calculateRemainingAmount().toFixed(2)}</small>
                                )}
                            </div>
                        </>
                    )}

                    <div className={styles.modalActions}>
                        <button type="button" className={styles.cancelBtn} onClick={() => setOpenEditModal(false)}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitBtn} disabled={loading}>
                            {loading ? 'Updating...' : 'Update Purchase'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/**
 * Purchase - Purchase Orders Management
 * Design: Shopify Polaris-inspired (matching DNS list design)
 */
function Purchase() {
    const { isFeatureEnabled } = useBusinessSettings();
    const uid = localStorage.getItem('id');
    const [purchases, setPurchases] = useState([]);
    const [thisMonthAmount, setThisMonthAmount] = useState(0);
    const [OpenEditModal, setOpenEditModal] = useState(false);
    const [OpenAddModal, setOpenAddModal] = useState(false);
    const [edit, setEdit] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        fetchPurchases();
    }, [refresh]);

    const fetchPurchases = async () => {
        console.log('DEBUG: Fetching purchases for uid:', uid);
        setLoading(true);
        try {
            const response = await AxiosUrl.get('/search-purchases', {
                params: {
                    uid,
                    page: 1,
                    limit: 1000,
                },
            });

            console.log('DEBUG: Fetch purchases response:', response.data);
            if (response.data.success) {
                console.log('DEBUG: Purchases loaded:', response.data.data.length, 'items');
                console.log('DEBUG: First 3 purchases:', response.data.data.slice(0, 3));
                setPurchases(response.data.data);
                setTotalCount(response.data.pagination.totalCount);
                setThisMonthAmount(response.data.summary.thisMonth);
            } else {
                console.error('DEBUG: Failed to fetch purchases:', response.data.message);
            }
        } catch (error) {
            console.error('DEBUG: Error fetching purchases:', error);
            console.error('Error fetching purchases:', error);
            toast.error('Failed to load purchases');
        } finally {
            setLoading(false);
        }
    };

    const deletePurchase = (id) => {
        const confirmDelete = window.confirm(
            'Are you sure you want to delete this purchase?'
        );
        if (confirmDelete) {
            AxiosUrl.post(`/delete-purchase/${uid}`, { id })
                .then((res) => {
                    if (res.data.success) {
                        toast.success(res.data.message || 'Purchase deleted successfully');
                    } else {
                        toast.error(res.data.message || 'Failed to delete purchase');
                    }
                    setRefresh(refresh + 1);
                })
                .catch(() => {
                    toast.error('Failed to delete the purchase.');
                });
        }
    };

    const formatCurrency = (amount) => {
        return `৳${parseFloat(amount || 0).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })}`;
    };

    const formatDate = (dateString) => {
        return Moment(dateString).format('MMM DD, YYYY');
    };

    const calculateTotal = (purchase) => {
        if (purchase.totalAmount) {
            return parseFloat(purchase.totalAmount);
        }
        const qty = parseFloat(purchase.Quantity || 0);
        const price = parseFloat(purchase.Price || 0);
        const extra = parseFloat(purchase.ExtraCost || 0);
        return qty * price + extra;
    };

    const pendingCount = purchases.filter((p) => p.Status === 1).length;
    const completedCount = purchases.filter((p) => p.Status === 0).length;

    return (
        <>
            <ToastContainer />
            <div className={styles.container}>
                <div className={styles.content}>
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            <h1 className={styles.title}>Purchase Orders</h1>
                            <p className={styles.subtitle}>Manage your inventory purchases and supplier orders</p>
                        </div>
                        <button className={styles.addBtn} onClick={() => setOpenAddModal(true)}>
                            <i className="fas fa-plus"></i>
                            Add Purchase Order
                        </button>
                    </div>

                    {/* Stats */}
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{totalCount}</div>
                            <div className={styles.statLabel}>Total Orders</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{formatCurrency(thisMonthAmount)}</div>
                            <div className={styles.statLabel}>This Month</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{pendingCount}</div>
                            <div className={styles.statLabel}>Pending</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={styles.statValue}>{completedCount}</div>
                            <div className={styles.statLabel}>Completed</div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>SUPPLIER</th>
                                    <th>PRODUCT</th>
                                    <th>QUANTITY</th>
                                    <th>UNIT PRICE</th>
                                    <th>TOTAL</th>
                                    <th>DATE</th>
                                    <th>STATUS</th>
                                    <th>ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" className={styles.loadingCell}>
                                            <div className={styles.spinner}></div>
                                            <p>Loading purchases...</p>
                                        </td>
                                    </tr>
                                ) : purchases.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className={styles.emptyCell}>
                                            <div className={styles.emptyIcon}><FiShoppingCart /></div>
                                            <h3>No Purchases Found</h3>
                                            <p>Click "Add Purchase Order" to create your first purchase</p>
                                        </td>
                                    </tr>
                                ) : (
                                    purchases.map((purchase) => (
                                        <tr key={purchase._id}>
                                            <td>
                                                <div className={styles.supplierName}>{purchase.Supplier}</div>
                                                {purchase.SupplierNumber && (
                                                    <div className={styles.subText}>{purchase.SupplierNumber}</div>
                                                )}
                                            </td>
                                            <td className={styles.productName}>{purchase.ProductName}</td>
                                            <td>{purchase.Quantity}</td>
                                            <td>{formatCurrency(purchase.Price)}</td>
                                            <td className={styles.totalAmount}>{formatCurrency(calculateTotal(purchase))}</td>
                                            <td>{formatDate(purchase.Date)}</td>
                                            <td>
                                                <span className={purchase.Status === 0 ? styles.statusCompleted : styles.statusPending}>
                                                    {purchase.Status === 0 ? 'Received' : 'Pending'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className={styles.actionBtns}>
                                                    <button
                                                        className={styles.editBtn}
                                                        onClick={() => {
                                                            setEdit(purchase);
                                                            setOpenEditModal(true);
                                                        }}
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button
                                                        className={styles.deleteBtn}
                                                        onClick={() => deletePurchase(purchase._id)}
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {OpenAddModal && (
                <AddPurchaseModal
                    setOpenAddModal={setOpenAddModal}
                    setRefresh={setRefresh}
                    refresh={refresh}
                />
            )}
            {OpenEditModal && (
                <EditPurchaseModal
                    setOpenEditModal={setOpenEditModal}
                    data={edit}
                    setRefresh={setRefresh}
                    refresh={refresh}
                />
            )}
        </>
    );
}

export default Purchase;
